import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, KeyRound, Pencil, ShieldCheck, Trash2 } from "lucide-react";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { CompactButton, CompactDangerButton } from "../../components/Button/Buttons";
import ConfirmDialog from "../../components/shared/ConfirmDialog/ConfirmDialog";
import Loader from "../../components/Loader/Loader";
import AlertBanner from "../../components/shared/AlertBanner/AlertBanner";
import { deleteProvider, listProviders } from "../../api/providers";
import UserAuthStore from "../../store/userStore";
import UIStore from "../../store/uiStore";
import ROUTES from "../../router/routes";
import logger from "../../services/Logger";
import AuthenticatedImage from "../../components/Common/AuthenticatedImage";
import PhotonLogo from "../../components/Common/PhotonLogo";

const PROVIDER_STATUS = {
  conflict: {
    label: "Needs attention",
    className: "bg-fill-red-50 text-ink-red-700 dark:bg-fill-red-900/20 dark:text-ink-red-300",
  },
  built_in: {
    label: "Built in",
    className: "bg-fill-blue-50 text-ink-blue-700 dark:bg-fill-blue-900/20 dark:text-ink-blue-300",
  },
  published: {
    label: "Published",
    className: "bg-fill-green-50 text-ink-green-700 dark:bg-fill-green-900/20 dark:text-ink-green-400",
  },
  unpublished: {
    label: "Unpublished",
    className: "bg-fill-gray-100 text-ink-gray-600 dark:bg-fill-gray-800 dark:text-ink-gray-400",
  },
  deleting: {
    label: "Deleting",
    className: "bg-fill-hex-fffbeb text-ink-amber-700 dark:bg-fill-hex-78350f/20 dark:text-ink-amber-400",
  },
  cleanup_failed: {
    label: "Cleanup failed",
    className: "bg-fill-red-50 text-ink-red-700 dark:bg-fill-red-900/20 dark:text-ink-red-400",
  },
};

// enabled=false doubles as a deletion tombstone, so deletion_status wins.
const getProviderStatus = (provider) => {
  if (provider.system_provider_conflict) return "conflict";
  if (provider.system_managed) return "built_in";
  if (provider.deletion_status === "deleting") return "deleting";
  if (provider.deletion_status === "cleanup_failed") return "cleanup_failed";
  return provider.enabled !== false ? "published" : "unpublished";
};

const ProviderLogo = ({ provider, orgId }) => {
  const isBuiltInPhoton = provider.provider_id === "photon" && provider.system_managed;

  return (
    <div className="w-9 h-9 rounded-lg border border-edge-gray-200 dark:border-edge-panel-border bg-surface-card-deep dark:bg-surface-card-deep flex items-center justify-center flex-shrink-0 overflow-hidden">
      {isBuiltInPhoton ? (
        <PhotonLogo className="w-6 h-6" />
      ) : (
        <AuthenticatedImage
          filePath={provider.icon_path}
          orgId={orgId}
          alt=""
          className="w-6 h-6 object-contain"
          fallback={<Building2 className="w-4 h-4 text-ink-gray-500 dark:text-ink-gray-400" />}
        />
      )}
    </div>
  );
};

const ForgeProvidersPage = () => {
  const navigate = useNavigate();
  const orgId = UserAuthStore((state) => state.orgId);
  const isAdmin = UIStore((state) => state.orgAvailable) === true;
  const orgAvailabilityKnown = UIStore((state) => state.orgAvailable) !== null;
  const [providers, setProviders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [providerToDelete, setProviderToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    if (orgAvailabilityKnown && !isAdmin) {
      navigate(ROUTES.SETTINGS.ROOT, { replace: true });
    }
  }, [isAdmin, navigate, orgAvailabilityKnown]);

  const loadProviders = useCallback(async () => {
    if (!orgId || !isAdmin) return;

    setIsLoading(true);
    setError(null);
    try {
      const providerList = await listProviders(orgId);
      const photonConflict = providerList.find(
        (provider) => provider.system_provider_conflict
      )?.system_provider_conflict;
      setProviders(providerList);
      setError(photonConflict?.message || null);
    } catch (err) {
      logger.error("ForgeProvidersPage", "Failed to load providers", {
        error: err.message,
        orgId,
      });
      setError(err.response?.data?.error || "Failed to load Forge providers.");
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, orgId]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const confirmDelete = async () => {
    if (!providerToDelete) return;

    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteProvider(providerToDelete.provider_id);
      setProviderToDelete(null);
      await loadProviders();
    } catch (err) {
      const responseData = err.response?.data;
      const dependentAgentIds = responseData?.dependent_agent_ids;
      setDeleteError(
        dependentAgentIds?.length
          ? `${responseData.error}: ${dependentAgentIds.join(", ")}`
          : responseData?.error || err.message || "Failed to delete provider."
      );
    } finally {
      setIsDeleting(false);
    }
  };

  if (!orgAvailabilityKnown || !isAdmin) return null;

  return (
    <SettingsPageShell
      title="Forge Providers"
      description="Register and secure the OAuth providers available to agents in your organization. Users connect their own accounts from Forge or chat."
      onBack={() => navigate(ROUTES.FORGE.ROOT)}
      action={{
        label: "Add Provider",
        onClick: () => navigate(ROUTES.SETTINGS.FORGE_PROVIDER_ADD),
      }}
      maxWidth="5xl"
    >
      <ConfirmDialog
        open={!!providerToDelete}
        title="Delete Provider?"
        error={deleteError}
        confirmLabel="Delete"
        loadingLabel="Deleting..."
        isLoading={isDeleting}
        onCancel={() => {
          setProviderToDelete(null);
          setDeleteError(null);
        }}
        onConfirm={confirmDelete}
        backdropClose={!isDeleting}
      >
        Delete "{providerToDelete?.name}" and its provider configuration, icon, and scopes? Providers referenced by agents cannot be deleted.
      </ConfirmDialog>

      {error && <AlertBanner className="mb-5">{error}</AlertBanner>}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : providers.length === 0 ? (
        <div className="border border-dashed border-edge-gray-300 dark:border-edge-panel-border rounded-lg px-6 py-14 text-center">
          <KeyRound className="w-8 h-8 mx-auto text-ink-gray-400 mb-3" />
          <p className="text-sm font-semibold text-ink-gray-900 dark:text-ink-gray-50">No Forge providers registered</p>
          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-1">Add an OAuth provider to make connected agents available to your organization.</p>
        </div>
      ) : (
        <div className="border border-edge-gray-200 dark:border-edge-panel-border rounded-lg overflow-hidden">
          <div className="hidden lg:grid lg:grid-cols-[minmax(240px,1.5fr)_minmax(120px,0.7fr)_minmax(190px,1fr)_90px_112px] gap-4 px-5 py-2.5 bg-surface-muted-empty dark:bg-surface-muted-empty border-b border-edge-gray-200 dark:border-edge-panel-border text-[10px] font-semibold uppercase text-ink-gray-500 dark:text-ink-gray-400">
            <span>Provider</span>
            <span>Service</span>
            <span>Credentials</span>
            <span>Status</span>
            <span className="text-right">Actions</span>
          </div>
          {providers.map((provider, index) => (
            <div
              key={provider.provider_id}
              className={`grid grid-cols-1 lg:grid-cols-[minmax(240px,1.5fr)_minmax(120px,0.7fr)_minmax(190px,1fr)_90px_112px] lg:items-center gap-3 lg:gap-4 px-5 py-4 bg-surface-card dark:bg-surface-card ${
                index > 0 ? "border-t border-edge-gray-200 dark:border-edge-panel-border" : ""
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <ProviderLogo provider={provider} orgId={orgId} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink-gray-900 dark:text-ink-gray-50 truncate">{provider.name}</p>
                  <p className="text-[11px] text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">
                    {provider.provider_id}
                  </p>
                </div>
              </div>

              <div className="text-xs text-ink-gray-700 dark:text-ink-gray-300">
                <p>{provider.service_type || "Not configured"}</p>
                {provider.auth_format && (
                  <p className="text-[10px] text-ink-gray-400 dark:text-ink-gray-500 mt-0.5">{provider.auth_format}</p>
                )}
              </div>

              <div className={`flex items-center gap-1.5 text-xs ${
                provider.system_managed
                  ? "text-ink-blue-700 dark:text-ink-blue-300"
                  : provider.has_credentials
                  ? "text-ink-green-700 dark:text-ink-green-400"
                  : "text-ink-amber-700 dark:text-ink-amber-400"
              }`}>
                <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
                {provider.system_managed
                  ? "Built in"
                  : provider.has_credentials ? "Secured" : "Not configured"}
              </div>

              <div>
                <span className={`inline-flex text-[10px] font-medium px-2 py-0.5 rounded-full ${
                  PROVIDER_STATUS[getProviderStatus(provider)].className
                }`}>
                  {PROVIDER_STATUS[getProviderStatus(provider)].label}
                </span>
              </div>

              <div className="flex items-center gap-3 lg:justify-end">
                {provider.system_managed || provider.system_provider_conflict ? (
                  <span className="text-[11px] text-ink-gray-500 dark:text-ink-gray-400">
                    {provider.system_provider_conflict ? "Reserved by Photon" : "Managed by Photon"}
                  </span>
                ) : (
                  <>
                    <CompactButton onClick={() => navigate(ROUTES.SETTINGS.FORGE_PROVIDER_EDIT(provider.provider_id))} aria-label={`Edit ${provider.name}`} title={`Edit ${provider.name}`}>
                      <Pencil aria-hidden="true" className="w-3.5 h-3.5" />
                    </CompactButton>
                    <CompactDangerButton onClick={() => setProviderToDelete(provider)} title={`Delete ${provider.name}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </CompactDangerButton>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </SettingsPageShell>
  );
};

export default ForgeProvidersPage;
